import type { ExternalProvider, MessageSyncCadence } from '@prisma/client';
import { z } from 'zod';

export const MESSAGE_SYNC_CADENCES = ['MANUAL', 'HOURLY', 'EVERY_15_MINUTES'] as const satisfies readonly MessageSyncCadence[];

export const GOOGLE_CALENDAR_EVENTS_SCOPE = 'https://www.googleapis.com/auth/calendar.events';
export const GOOGLE_CALENDAR_READONLY_SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';
export const GOOGLE_DRIVE_READONLY_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
export const GOOGLE_GMAIL_MODIFY_SCOPE = 'https://www.googleapis.com/auth/gmail.modify';

export const GOOGLE_MEMBER_EXTERNAL_SCOPES = [
  GOOGLE_CALENDAR_EVENTS_SCOPE,
  GOOGLE_CALENDAR_READONLY_SCOPE,
  GOOGLE_DRIVE_READONLY_SCOPE,
  GOOGLE_GMAIL_MODIFY_SCOPE,
] as const;

export const MICROSOFT_USER_READ_SCOPE = 'https://graph.microsoft.com/User.Read';
export const MICROSOFT_MAIL_READWRITE_SCOPE = 'https://graph.microsoft.com/Mail.ReadWrite';
export const MICROSOFT_MAIL_SEND_SCOPE = 'https://graph.microsoft.com/Mail.Send';
export const MICROSOFT_CALENDAR_READWRITE_SCOPE = 'https://graph.microsoft.com/Calendars.ReadWrite';
export const MICROSOFT_MAILBOX_SETTINGS_READWRITE_SCOPE = 'https://graph.microsoft.com/MailboxSettings.ReadWrite';

export const MICROSOFT_MEMBER_EXTERNAL_SCOPES = [
  'openid',
  'profile',
  'email',
  'offline_access',
  MICROSOFT_USER_READ_SCOPE,
  MICROSOFT_MAIL_READWRITE_SCOPE,
  MICROSOFT_MAIL_SEND_SCOPE,
  MICROSOFT_CALENDAR_READWRITE_SCOPE,
  MICROSOFT_MAILBOX_SETTINGS_READWRITE_SCOPE,
] as const;

const memberExternalGoogleCallbackSuccessQuerySchema = z.object({
  code: z.string().trim().min(1),
  state: z.string().trim().min(1),
  error: z.undefined().optional(),
  error_description: z.undefined().optional(),
});

const memberExternalGoogleCallbackErrorQuerySchema = z.object({
  error: z.string().trim().min(1),
  error_description: z.string().trim().min(1).optional(),
  state: z.string().trim().min(1).optional(),
  code: z.undefined().optional(),
});

export const memberExternalGoogleCallbackQuerySchema = z.union([
  memberExternalGoogleCallbackSuccessQuerySchema,
  memberExternalGoogleCallbackErrorQuerySchema,
]);

export type MemberExternalGoogleCallbackQuery = z.infer<typeof memberExternalGoogleCallbackQuerySchema>;

export type ExternalConnectProvider = 'google' | 'microsoft';

export const EXTERNAL_CONNECT_BROADCAST_CHANNEL = 'vivport:external-connect';

export interface ExternalConnectBroadcastMessage {
  type: `vivport:${ExternalConnectProvider}-member-external-connect`;
  provider: ExternalConnectProvider;
  status: 'success' | 'error';
  reason?: string;
}

export interface MemberExternalAccountSyncSummary {
  oauthLinkedAt?: string;
  disconnectedAt?: string;
  tokenType?: string;
  tokenRefreshedAt?: string;
  lastMessageManualImportAt?: string;
  lastCalendarManualImportAt?: string;
  lastMessageManualImportSummary?: {
    importedCount: number;
    updatedCount: number;
    skippedCount: number;
    fetchedCount: number;
    seededMessageCount: number;
    expandedThreadCount: number;
    expandedMessageCount: number;
  };
  lastCalendarManualImportSummary?: {
    importedCount: number;
    updatedCount: number;
    skippedCount: number;
    fetchedCount: number;
  };
}

export interface MemberExternalAccountMessageSyncStatus {
  lastCompletedAt: string | null;
  needsReauth: boolean;
  needsReauthReason: string | null;
}

export type MessageSyncBeaconOutcome = 'EMITTED' | 'NOT_DUE' | 'MANUAL' | 'NEEDS_REAUTH';

export interface MemberExternalAccountMessageSyncBeaconResult {
  accounts: Array<{
    memberExternalAccountId: number;
    externalEmail: string;
    outcome: MessageSyncBeaconOutcome;
  }>;
  emittedCount: number;
}

export interface MemberExternalAccountSafe {
  id: number;
  provider: ExternalProvider;
  externalEmail: string;
  productLabel: string;
  externalUserId: string | null;
  tokenExpiry: string | null;
  scopes: string[];
  isActive: boolean;
  calendarImportCapable: boolean;
  calendarWriteCapable: boolean;
  outboundCapable: boolean;
  labelWriteCapable: boolean;
  messageCount: number;
  isDefaultOutbound: boolean;
  eventCount: number;
  isDefaultCalendarWrite: boolean;
  syncSummary: MemberExternalAccountSyncSummary | null;
  messageSync: MemberExternalAccountMessageSyncStatus;
  importPreferences: MemberExternalAccountImportPreferencesSafe;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleMemberExternalAuthStartResult {
  authUrl: string;
}

export interface MemberExternalAccountListResult {
  items: MemberExternalAccountSafe[];
}

export interface MemberExternalAccountCalendarsSource {
  memberExternalAccountId: number;
  provider: ExternalProvider;
  externalEmail: string;
}

export interface MemberExternalAccountCalendar {
  providerCalendarId: string;
  name: string;
  isPrimary: boolean;
}

export interface MemberExternalAccountCalendarsResult {
  source: MemberExternalAccountCalendarsSource;
  items: MemberExternalAccountCalendar[];
}

export interface MemberExternalAccountDisconnectResult {
  id: number;
  disconnected: true;
}

export const memberExternalAccountMessageManualImportSchema = z.object({
  lookbackDays: z.number().int().min(1).max(3650).nullable().optional(),
  maxResults: z.number().int().min(1).max(500).optional(),
  onlyWithAttachments: z.boolean().optional(),
  includeSpamTrash: z.boolean().optional(),
  extractObligationsOnImport: z.boolean().optional(),
  autoFileOnImport: z.boolean().optional(),
  excludeNoReply: z.boolean().optional(),
  excludedSenders: z
    .array(z.string().trim().toLowerCase().min(3).max(320))
    .max(200)
    .transform((value) => [...new Set(value)])
    .optional(),
  excludedDomains: z
    .array(z.string().trim().toLowerCase().min(3).max(320))
    .max(200)
    .transform((value) => [...new Set(value)])
    .optional(),
  pageToken: z.string().trim().min(1).max(512).optional(),
}).strict();

export type MemberExternalAccountMessageManualImportInput = z.infer<typeof memberExternalAccountMessageManualImportSchema>;

export interface MemberExternalAccountImportPreferencesSafe {
  messageLookbackDays: number | null;
  messageMaxResults: number;
  messageIncludeSpamTrash: boolean;
  messageOnlyWithAttachments: boolean;
  extractObligationsOnImport: boolean;
  autoFileOnImport: boolean;
  messageExcludeNoReply: boolean;
  messageExcludedSenders: string[];
  messageExcludedDomains: string[];
  messageSyncCadence: MessageSyncCadence;
  calendarLookbackDays: number;
  calendarLookaheadDays: number;
  calendarMaxResults: number;
}

export const memberExternalAccountImportPreferencesUpdateSchema = z.object({
  messageLookbackDays: z.number().int().min(1).max(3650).nullable().optional(),
  messageMaxResults: z.number().int().min(1).max(500).optional(),
  messageIncludeSpamTrash: z.boolean().optional(),
  messageOnlyWithAttachments: z.boolean().optional(),
  extractObligationsOnImport: z.boolean().optional(),
  autoFileOnImport: z.boolean().optional(),
  messageExcludeNoReply: z.boolean().optional(),
  messageExcludedSenders: z
    .array(z.string().trim().toLowerCase().min(3).max(320))
    .max(200)
    .transform((value) => [...new Set(value)])
    .optional(),
  messageExcludedDomains: z
    .array(z.string().trim().toLowerCase().min(3).max(320))
    .max(200)
    .transform((value) => [...new Set(value)])
    .optional(),
  messageSyncCadence: z.enum(MESSAGE_SYNC_CADENCES).optional(),
  calendarLookbackDays: z.number().int().min(0).max(3650).optional(),
  calendarLookaheadDays: z.number().int().min(0).max(3650).optional(),
  calendarMaxResults: z.number().int().min(1).max(500).optional(),
}).strict();

export type MemberExternalAccountImportPreferencesUpdateInput = z.infer<
  typeof memberExternalAccountImportPreferencesUpdateSchema
>;

export interface MemberExternalAccountMessageManualImportSource {
  memberExternalAccountId: number;
  provider: ExternalProvider;
  externalEmail: string;
}

export interface MemberExternalAccountMessageManualImportResult {
  source: MemberExternalAccountMessageManualImportSource;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  fetchedCount: number;
  seededMessageCount: number;
  expandedThreadCount: number;
  expandedMessageCount: number;
  importedProviderThreadIds: string[];
  contentIds: number[];
  failures: {
    writeFailures: number;
    taggingFailures: number;
    policyFailures: number;
  };
  createdContentIds: number[];
  updatedContentIds: number[];
  createdFileContentIds: number[];
  nextPageToken: string | null;
}

export const memberExternalAccountCalendarManualImportSchema = z.object({
  timeMin: z.string().datetime().optional(),
  timeMax: z.string().datetime().optional(),
  maxResults: z.number().int().min(1).max(500).optional(),
}).refine((value) => {
  if (!value.timeMin || !value.timeMax) {
    return true;
  }
  return new Date(value.timeMin).getTime() < new Date(value.timeMax).getTime();
}, {
  message: 'timeMax must be after timeMin',
  path: ['timeMax'],
});

export type MemberExternalAccountCalendarManualImportInput =
  z.infer<typeof memberExternalAccountCalendarManualImportSchema>;

export interface MemberExternalAccountCalendarManualImportWindow {
  timeMin: string;
  timeMax: string;
}

export interface MemberExternalAccountCalendarManualImportSource {
  memberExternalAccountId: number;
  provider: ExternalProvider;
  externalEmail: string;
}

export interface MemberExternalAccountCalendarManualImportResult {
  source: MemberExternalAccountCalendarManualImportSource;
  window: MemberExternalAccountCalendarManualImportWindow;
  importedCount: number;
  updatedCount: number;
  skippedCount: number;
  fetchedCount: number;
  truncation: {
    hasMoreAvailable: boolean;
    maxResultsReached: boolean;
  };
  conflicts: {
    crossHubProviderCollision: number;
  };
  failures: {
    writeFailures: number;
    taggingFailures: number;
    policyFailures: number;
  };
  createdContentIds: number[];
  updatedContentIds: number[];
}
